import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { getTicketWaiverRecord, type TicketWaiverType } from '@/lib/api/tickets.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';

function parseWaiverType(raw: string | undefined): TicketWaiverType | null {
  if (raw === 'pre_repair' || raw === 'post_repair') return raw;
  return null;
}

export function TicketWaiverRecordPage() {
  const { id, waiverType: waiverTypeParam } = useParams<{ id: string; waiverType: string }>();
  const waiverType = parseWaiverType(waiverTypeParam);

  const recordQuery = useQuery({
    queryKey: ['ticket-waiver-record', id, waiverType],
    queryFn: () => getTicketWaiverRecord(id!, waiverType!),
    enabled: Boolean(id && waiverType),
  });

  if (!id || !waiverType) {
    return (
      <p className="mx-auto max-w-3xl p-8 text-center text-slate-600">Invalid waiver record URL.</p>
    );
  }

  if (recordQuery.isLoading) {
    return <div className="mx-auto max-w-3xl p-8 text-center text-slate-600">Loading signed waiver…</div>;
  }

  if (recordQuery.isError || !recordQuery.data) {
    const parsed = recordQuery.error ? parseApiError(recordQuery.error) : null;
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-8">
        <p className="text-red-600">{parsed?.message ?? 'Could not load waiver record.'}</p>
        <Link to={`/tickets/${id}`} className="text-sm font-semibold text-primary hover:underline">
          ← Back to ticket
        </Link>
      </div>
    );
  }

  const record = recordQuery.data;

  return (
    <div className="min-h-screen bg-white text-slate-900 print:bg-white">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-3">
        <Link to={`/tickets/${id}`} className="text-sm font-semibold text-primary hover:underline">
          ← Back to ticket {record.ticketNumber}
        </Link>
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477]"
          onClick={() => window.print()}
        >
          Print / Save as PDF
        </button>
      </div>

      <article className="mx-auto max-w-3xl px-6 py-10 print:max-w-none print:px-8 print:py-6">
        <header className="border-b border-slate-300 pb-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">{record.storeName}</p>
          {record.storeAddress ? <p className="mt-1 text-sm text-slate-600">{record.storeAddress}</p> : null}
          {record.storePhone ? <p className="text-sm text-slate-600">{record.storePhone}</p> : null}
          <h1 className="mt-4 text-2xl font-bold text-slate-950">{record.waiverTitle}</h1>
          <p className="mt-2 text-sm text-slate-600">
            Ticket <span className="font-semibold text-slate-900">{record.ticketNumber}</span>
            {' · '}
            Signed {formatDateTime(record.signedAt)}
            {' · '}
            Version {record.waiverVersion}
          </p>
        </header>

        <section className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 print:border-slate-300">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Customer &amp; devices</h2>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-slate-500">Customer</dt>
              <dd className="font-semibold text-slate-900">{record.customerName}</dd>
            </div>
            {record.customerPhone ? (
              <div>
                <dt className="text-slate-500">Phone</dt>
                <dd className="font-semibold text-slate-900">{record.customerPhone}</dd>
              </div>
            ) : null}
          </dl>
          {record.devices.length > 0 ? (
            <div className="mt-4 space-y-4">
              {record.devices.map((device) => (
                <div key={device.label}>
                  <p className="font-semibold text-slate-900">{device.label}</p>
                  {device.repairs.length > 0 ? (
                    <ul className="mt-1 space-y-1 text-sm text-slate-700">
                      {device.repairs.map((repair) => (
                        <li key={`${device.label}-${repair.name}`} className="flex justify-between gap-4">
                          <span>{repair.name}</span>
                          <span className="shrink-0 font-medium">{formatCurrency(repair.price)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">No services listed</p>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          <div className="mt-4 flex justify-between border-t border-slate-200 pt-3 text-sm font-bold">
            <span>Ticket total</span>
            <span>{formatCurrency(record.total)}</span>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Agreement text</h2>
          <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
            {record.waiverContent}
          </div>
        </section>

        <section className="mt-10 border-t border-slate-300 pt-8">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">Customer signature</h2>
          <p className="mt-1 text-sm text-slate-600">
            {record.customerName} — {formatDateTime(record.signedAt)}
          </p>
          <div className="mt-4 rounded-xl border border-slate-300 bg-white p-4 print:break-inside-avoid">
            <img
              src={record.signatureDataUrl}
              alt={`Signature for ticket ${record.ticketNumber}`}
              className="mx-auto max-h-40 w-full max-w-lg object-contain"
            />
          </div>
        </section>

        <footer className="mt-10 border-t border-slate-200 pt-4 text-xs text-slate-500 print:mt-8">
          This record ties waiver version {record.waiverVersion} to ticket {record.ticketNumber} at{' '}
          {record.storeName}. Generated from Repairiz.
        </footer>
      </article>

      <style>{`
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
